import { Link, useNavigate } from "@tanstack/react-router";
import { ShoppingBag, Search, User, X, Heart, Menu, ChevronDown, LogOut, Sparkles, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { checkIsAdmin } from "@/lib/admin.functions";
import { categoryCountsQueryOptions } from "@/lib/categories.functions";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import logoAsset from "@/assets/kupish-logo.png.asset.json";

const ADMIN_MENU = [
  { label: "Pregled in statistika", tab: undefined },
  { label: "Prodajalci", tab: "vendors" },
  { label: "Izdelki", tab: "products" },
  { label: "Naročila", tab: "orders" },
  { label: "Naročnine", tab: "subscriptions" },
  { label: "Prejemniki", tab: "mailing" },
  { label: "Kampanje", tab: "campaigns" },
  { label: "Facebook objave", tab: "facebook" },
  { label: "Dnevnik sprememb", tab: "audit" },
  { label: "404 strani", tab: "notfound" },
  { label: "Nastavitve", tab: "settings" },
] as const;


type MenuItem = { label: string; to: string; slug?: string; children?: { label: string; slug: string }[] };

const STATIC_MENU: MenuItem[] = [
  { label: "Vsi izdelki", to: "/izdelki" },
  {
    label: "Drogerija",
    to: "/kategorije/$slug",
    slug: "drogerija",
    children: [
      { label: "Nega doma", slug: "nega-doma" },
      { label: "Pripomočki za čiščenje", slug: "pripomocki-za-ciscenje" },
      { label: "Čistila", slug: "cistila" },
      { label: "Krpe za čiščenje", slug: "krpe-za-ciscenje" },
      { label: "Antibakterijski izdelki", slug: "antibakterijski-izdelki" },
      { label: "Osebna nega", slug: "osebna-nega" },
    ],
  },
  {
    label: "Elektronika & Avdio",
    to: "/kategorije/$slug",
    slug: "elektronika-avdio",
    children: [
      { label: "Avdio & Video", slug: "avdio-video" },
      { label: "Zabavna elektronika", slug: "zabavna-elektronika" },
      { label: "Slušalke", slug: "slusalke" },
      { label: "Telefonija", slug: "telefonija" },
      { label: "Prenosni zvočniki", slug: "prenosni-zvocniki" },
      { label: "Nadzorne kamere", slug: "nadzorne-kamere" },
      { label: "Otroške varuške", slug: "otroske-varuske" },
      { label: "Športne kamere", slug: "sportne-kamere" },
    ],
  },
  {
    label: "Avto elektronika",
    to: "/kategorije/$slug",
    slug: "avto-elektronika",
    children: [
      { label: "Avto-kamere", slug: "avto-kamere" },
      { label: "Prostoročno telefoniranje", slug: "prostorocno-telefoniranje" },
      { label: "FM radiji", slug: "fm-radiji" },
      { label: "Testerji alkoholiziranosti", slug: "testerji-alkoholiziranosti" },
    ],
  },
  {
    label: "Avtomobilska oprema",
    to: "/kategorije/$slug",
    slug: "avtomobilska-oprema",
    children: [
      { label: "Napajalniki in polnilci", slug: "napajlniki-in-polnilci" },
      { label: "Nosilci in držala", slug: "nosilci-in-drzala" },
    ],
  },
  {
    label: "Dom & Vrt",
    to: "/kategorije/$slug",
    slug: "dom-vrt",
    children: [
      { label: "Vrt", slug: "vrt" },
      { label: "Kuhinja", slug: "kuhinja" },
      { label: "Prti", slug: "prti" },
      { label: "Stojala", slug: "stojala" },
    ],
  },
  {
    label: "Moda & dodatki",
    to: "/kategorije/$slug",
    slug: "moda-dodatki",
    children: [
      { label: "Nakit", slug: "nakit" },
    ],
  },
  {
    label: "Šport in prosti čas",
    to: "/kategorije/$slug",
    slug: "sport-prosti-cas",
  },
];


function BrandDivider({ className }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-3 px-4 ${className}`}>
      <span className="h-px flex-1 bg-border" />
      <img src="/favicon.png" alt="" aria-hidden="true" width={18} height={18} className="h-[18px] w-[18px] rounded-full" />
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

export function SiteHeader() {
  const { count } = useCart();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSearchOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    setSearchOpen(false);
    navigate({ to: "/izdelki", search: term ? { q: term } : {} });
    setQ("");
  };

  const toggleGroup = (label: string) =>
    setOpenGroups((s) => ({ ...s, [label]: !s[label] }));

  const closeMobile = () => {
    setMobileOpen(false);
    setOpenGroups({});
  };

  const checkAdmin = useServerFn(checkIsAdmin);
  const adminCheck = useQuery({
    queryKey: ["admin-access", user?.id],
    queryFn: () => checkAdmin(),
    enabled: !!user,
    retry: false,
    staleTime: 10 * 60_000,
  });
  const isAdmin = adminCheck.data?.isAdmin === true;

  const { data: counts } = useSuspenseQuery(categoryCountsQueryOptions());

  const visibleMenu = useMemo(() => {
    return STATIC_MENU.map((item) => {
      if (!item.slug) return item;
      const children = item.children?.filter((c) => (counts[c.slug] ?? 0) > 0);
      const hasProducts = (counts[item.slug] ?? 0) > 0;
      if (!hasProducts && (!children || children.length === 0)) return null;
      return { ...item, children };
    }).filter(Boolean) as MenuItem[];
  }, [counts]);


  return (
    <header className="sticky top-0 z-40 bg-background border-b border-border md:bg-background/85 md:backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-3 md:px-6 h-14 sm:h-16 md:h-20 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 shrink-0">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Odpri meni"
                className="lg:hidden p-2 -ml-2 hover:text-accent transition-colors"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85vw] max-w-sm p-0 overflow-y-auto">
              <SheetTitle className="sr-only">Meni</SheetTitle>
              <div className="px-5 py-5 border-b border-border">
                <Link to="/" onClick={closeMobile} className="flex items-center">
                  <img src={logoAsset.url} alt="kupish.si" width={160} height={56} decoding="async" className="h-10 w-auto" />
                </Link>
              </div>
              <nav className="py-2">
                {visibleMenu.map((item) => {
                  if (!item.children?.length) {
                    return (
                      <Link
                        key={item.label}
                        to={item.to}
                        onClick={closeMobile}
                        className="block px-5 py-3 text-sm uppercase tracking-[0.12em] font-medium border-b border-border hover:bg-muted hover:text-accent"
                      >
                        {item.label}
                      </Link>
                    );
                  }
                  const open = !!openGroups[item.label];
                  return (
                    <div key={item.label} className="border-b border-border">
                      <div className="flex items-stretch">
                        <Link
                          to={item.to}
                          params={{ slug: item.slug! }}
                          onClick={closeMobile}
                          className="flex-1 px-5 py-3 text-sm uppercase tracking-[0.12em] font-medium hover:text-accent"
                        >
                          {item.label}
                        </Link>
                        <button
                          aria-label={open ? "Zapri podmeni" : "Odpri podmeni"}
                          aria-expanded={open}
                          onClick={() => toggleGroup(item.label)}
                          className="px-4 hover:text-accent"
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
                          />
                        </button>
                      </div>
                      {open && (
                        <div className="pb-2 bg-muted/40">
                          {item.children.map((child) => (
                            <Link
                              key={child.slug}
                              to="/kategorije/$slug"
                              params={{ slug: child.slug }}
                              onClick={closeMobile}
                              className="block px-8 py-2.5 text-sm hover:text-accent"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
              <div className="px-5 py-4 mt-2 space-y-1 text-sm">
                <BrandDivider className="my-3" />
                <Link to={user ? "/dashboard" : "/prijava"} onClick={closeMobile} className="flex items-center gap-2 py-2 hover:text-accent">
                  <User className="h-4 w-4" />
                  Moj račun
                </Link>
                <Link to={user ? "/zelje" : "/prijava"} onClick={closeMobile} className="flex items-center gap-2 py-2 hover:text-accent">
                  <Heart className="h-4 w-4" />
                  Seznam želja
                </Link>
                <Link to="/kosarica" onClick={closeMobile} className="flex items-center gap-2 py-2 hover:text-accent">
                  <ShoppingBag className="h-4 w-4" />
                  Moja košarica
                  {count > 0 && (
                    <span className="ml-auto bg-accent text-accent-foreground text-[10px] font-medium rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                      {count}
                    </span>
                  )}
                </Link>
                {user && (
                  <Link to="/narocila" onClick={closeMobile} className="flex items-center gap-2 py-2 hover:text-accent">
                    <ShoppingBag className="h-4 w-4" />
                    Moja naročila
                  </Link>
                )}
                {user ? (
                  <button
                    onClick={async () => {
                      await signOut();
                      closeMobile();
                    }}
                    className="flex w-full items-center gap-2 py-2 text-left hover:text-accent"
                  >
                    <LogOut className="h-4 w-4" />
                    Odjava
                  </button>
                ) : (
                  <Link to="/prijava" onClick={closeMobile} className="flex items-center gap-2 py-2 hover:text-accent">
                    <LogOut className="h-4 w-4 rotate-180" />
                    Prijava
                  </Link>
                )}
              </div>
              <div className="px-5 py-4 space-y-1 text-sm border-t border-border">
                <div className="flex items-center gap-2 py-2 text-accent">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-accent/30 bg-accent/10">
                    <Heart className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-serif text-base tracking-tight">Obiskovalci</span>
                </div>
                <Link to="/izdelki" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Vsi izdelki
                </Link>
                <Link to="/kategorije" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Kategorije
                </Link>
                <Link to="/navdih" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Navdih & zgodbe
                </Link>
              </div>
              <div className="px-5 py-4 space-y-1 text-sm border-t border-border">
                <div className="flex items-center gap-2 py-2 text-accent">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-accent/30 bg-accent/10">
                    <Sparkles className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-serif text-base tracking-tight">Prodajalci</span>
                </div>
                <Link to="/registracija" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Registracija prodajalca
                </Link>
                <Link to="/prijava" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Prijava prodajalca
                </Link>
                <Link to="/dashboard" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Nadzorna plošča
                </Link>
                <Link to="/najem-spletne-trgovine" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Najem spletne trgovine
                </Link>
                <Link to="/placilo-najemnine" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Plačilo letne najemnine
                </Link>
                <Link to="/predstavitev-prodajalca" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Predstavitev ponudnika
                </Link>
                <Link to="/uporabniska-navodila" onClick={closeMobile} className="block py-2 hover:text-accent">
                  Uporabniška navodila
                </Link>
              </div>
              {isAdmin && (
                <div className="px-5 py-4 space-y-1 text-sm border-t border-border">
                  <div className="flex items-center gap-2 py-2 text-accent">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-accent/30 bg-accent/10">
                      <ShieldCheck className="h-3.5 w-3.5" />
                    </span>
                    <span className="font-serif text-base tracking-tight">Admin</span>
                  </div>
                  {ADMIN_MENU.map((item) => (
                    <Link
                      key={item.label}
                      to="/admin"
                      search={item.tab ? { tab: item.tab } : {}}
                      onClick={closeMobile}
                      className="block py-2 hover:text-accent"
                    >
                      {item.label}
                    </Link>
                  ))}
                  <Link to="/admin/orodja" onClick={closeMobile} className="block py-2 hover:text-accent">
                    Strateška orodja
                  </Link>
                  <Link to="/migracija-slik" onClick={closeMobile} className="block py-2 hover:text-accent">
                    Migracija slik → WebP
                  </Link>
                </div>
              )}

            </SheetContent>
          </Sheet>
          <Link to="/" className="flex items-center">
            <img src={logoAsset.url} alt="kupish.si" width={272} height={72} decoding="async" className="h-8 sm:h-9 md:h-12 w-auto shrink-0" />
          </Link>
        </div>
        <nav className="hidden lg:flex items-center gap-7 text-[13px] uppercase tracking-[0.14em] font-medium whitespace-nowrap">
          {visibleMenu.map((item) => {
            const trigger = item.slug ? (
              <Link
                to={item.to}
                params={{ slug: item.slug }}
                className="hover:text-accent transition-colors py-6 whitespace-nowrap"
              >
                {item.label}
              </Link>
            ) : (
              <Link to={item.to} className="hover:text-accent transition-colors py-6 whitespace-nowrap">
                {item.label}
              </Link>
            );

            if (!item.children?.length) {
              return <div key={item.label}>{trigger}</div>;
            }

            return (
              <div key={item.label} className="relative group">
                {trigger}
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  <div className="min-w-[200px] bg-background border border-border shadow-lg py-2 normal-case tracking-normal text-sm font-normal">
                    {item.children.map((child) => (
                      <Link
                        key={child.slug}
                        to="/kategorije/$slug"
                        params={{ slug: child.slug }}
                        className="block px-4 py-2 hover:bg-muted hover:text-accent transition-colors"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
        <div className="flex items-center gap-0.5 md:gap-1.5 text-foreground shrink-0">
          <button aria-label="Iskanje" title="Iskanje" onClick={() => setSearchOpen(true)} className="p-2.5 hover:text-accent transition-colors">
            <Search className="h-[18px] w-[18px]" />
          </button>
          <Link to={user ? "/dashboard" : "/prijava"} aria-label="Moj račun" title="Moj račun" className="p-2.5 hover:text-accent transition-colors">
            <User className="h-[18px] w-[18px]" />
          </Link>
          <Link to={user ? "/zelje" : "/prijava"} aria-label="Seznam želja" title="Seznam želja" className="hidden lg:inline-flex p-2 hover:text-accent transition-colors">
            <Heart className="h-[18px] w-[18px]" />
          </Link>
          <Link to="/kosarica" className="relative p-2.5 hover:text-accent transition-colors" aria-label="Košarica" title="Košarica">
            <ShoppingBag className="h-[18px] w-[18px]" />
            {count > 0 && (
              <span className="absolute top-0.5 right-0.5 bg-accent text-accent-foreground text-[10px] font-medium rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm" onClick={() => setSearchOpen(false)}>
          <div className="mx-auto max-w-3xl px-6 pt-32" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={submitSearch} className="relative">
              <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                autoFocus
                aria-label="Iskalni niz"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Poišči izdelek…"
                maxLength={200}
                className="w-full bg-transparent border-0 border-b border-border pl-8 pr-10 py-4 font-serif text-2xl md:text-3xl focus:outline-none focus:border-foreground"
              />
              <button type="button" onClick={() => setSearchOpen(false)} aria-label="Zapri" className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </form>
            <p className="mt-4 text-xs text-muted-foreground">Pritisni Enter za iskanje, Esc za zapri.</p>
          </div>
        </div>
      )}
    </header>
  );
}
